import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import OutlinedInput from '@mui/material/OutlinedInput';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import ListSubheader from '@mui/material/ListSubheader';
import ClearIcon from '@mui/icons-material/Clear';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ElipsisText from '../ElipsisText';
import Checkbox from '../Checkbox'
import './index.scss';
import Input from '../Input';

interface DropdownProps {
  id: string;
  values: any[];
  options: {
    label: string;
    value: string;
    disabled?: boolean;
  }[];
  allOptions: {
    label: string;
    value: string;
    disabled?: boolean;
  }[];
  label: string;
  required?: boolean;
  name: string;
  multiSelect?: boolean;
  placeholder?: string;
  rootClassName?: string;
  inputClass?: string;
  disabled?: boolean;
  noSearch?: boolean;
  disabledWithoutPointerEvents?: boolean;
  error?: boolean;
  errorMessage?: string;
  searchText?: string;
  onChange: (_: any, name: string) => void;
  onBlur?: (_: any, name: string) => void;
  onSearch?: (_: any, name: string) => void;
  disableClearable?: boolean;
  labelClassName?: string;
}

const Dropdown: React.FC<DropdownProps> = ({
  id, values, placeholder, options, allOptions, label, required, name, multiSelect, rootClassName, inputClass, disabled, noSearch, disabledWithoutPointerEvents, error, errorMessage, searchText, onChange, onBlur, onSearch, labelClassName, disableClearable
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState<boolean>(false);
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const prevOpenRef = useRef<boolean>(false);

  const clearSearch = () => {
    onSearch && onSearch({ target: { value: '' } }, name)
  }

  const CustomDropdownIcon = (props: any) => (
    <KeyboardArrowDownIcon
      {...props}
      className={clsx('dropdown-custom-icon me-2 cursor-pointer', disabled && 'dropdown-custom-icon-disabled')}
      onClick={() => setOpen(true)}
    />
  );

  useEffect(() => {
    const prevOpen = prevOpenRef.current;
    if (!prevOpen && open && !noSearch) {
      setTimeout(() => {
        const el = document.getElementById(id) as HTMLInputElement | null;
        el?.focus();
      }, 100);
    }
    prevOpenRef.current = open;
  }, [open, id, noSearch]);

  useEffect(() => {
    if (open && !noSearch && isSearching) {
      const el = document.getElementById(id) as HTMLInputElement | null;
      if (el && document.activeElement !== el) {
        el.focus();
      }
    }
  }, [options, open, id, noSearch, isSearching]);

  return (
    <div className={`d-dropdown ${rootClassName}`}>
      <FormControl
        classes={{
          root: 'form-control p-0 border-none'
        }}
      >
        <InputLabel
          id={`dropdown-label-for${name}`}
          classes={{
            root: `dropdown-label ${labelClassName || ''} ${values.length ? 'dropdown-label-values' : ''} ${disabled ? 'dropdown-label-disabled' : ''} ${error ? 'dropdown-label-error' : ''}`,
            focused: 'dropdown-label-focused'
          }}
        >
          {label}
          {required && <span className='text-danger ms-1'>*</span>}
        </InputLabel>
        <Select
          displayEmpty
          labelId={`dropdown-label-for${name}`}
          value={values}
          disabled={disabled}
          open={open}
          IconComponent={CustomDropdownIcon}
          onChange={(e) => {
            setIsSearching(false);
            onChange(e, name);
            if (!multiSelect) {
              clearSearch();
            }
          }}
          onBlur={(e) => { setOpen(false); onBlur && onBlur(multiSelect ? e : { ...e, target: { value: e?.target?.value[0] ? e?.target?.value?.[0].toString() : '' } }, name) }}
          multiple={!!multiSelect}
          data-testid={`select-${name}`}
          input={
            <OutlinedInput
              classes={{
                root: `dropdown-select-input ${open ? 'dropdown-active' : ''} ${error && 'dropdown-is-invalid'} ${disabled ? 'dropdown-disabled' : ''} ${disabledWithoutPointerEvents ? 'dropdown-disabled-no-pointer-events' : ''} ${inputClass}`,
                notchedOutline: 'dropdown-select-outline',
              }}
              endAdornment={
                !disabled && values.length > 0 && !disableClearable && (
                  <ClearIcon
                    className='dropdown-clear-icon cursor-pointer'
                    onClick={(e) => {
                      e.stopPropagation();
                      onChange({ target: { value: multiSelect ? [] : '' } }, name);
                      onBlur && onBlur({ target: { value: multiSelect ? [] : '' } }, name);
                      clearSearch();
                    }}
                  />
                )
              }
            />
          }
          renderValue={(selected) => {
            if (selected && selected.length === 0) {
              return <span className={clsx('dropdown-placeholder', error && 'dropdown-placeholder-error', disabled && 'dropdown-placeholder-disabled')}>{placeholder} {required && <span className="text-danger">*</span>}</span>;
            } else {
              if (multiSelect) {
                const values = allOptions.filter(option => selected.includes(option.value)).map(item => item.label);
                return <ElipsisText className={clsx(disabled && 'dropdown-values-disabled')} text={values.join(', ')} width="100%" />
              } else {
                const selectedOption = allOptions.find((option) => option.value == selected[0]);
                return <ElipsisText className={clsx(disabled && 'dropdown-values-disabled')} text={selectedOption?.label ?? ''} width="100%" />;
              }
            }
          }}
          MenuProps={{
            classes: {
              paper: 'dropdown-menu-popover-root',
              list: 'dropdown-select-menu-list'
            },
          }}
          onOpen={() => setOpen(true)}
          onClose={() => { setOpen(false); setIsSearching(false); clearSearch(); }}
        >
          {
            !noSearch && (
              <ListSubheader className="dropdown-search-input-wrapper" disableSticky>
                <Input
                  type="search"
                  className="form-control-sm"
                  id={id}
                  name={name}
                  placeholder={t("common.dropdown.search")}
                  value={searchText}
                  onKeyDown={(e) => e.stopPropagation()}
                  onChange={e => {
                    setIsSearching(true);
                    onSearch && onSearch(e, name);
                  }}
                />
              </ListSubheader>
            )
          }

          {
            options && options.length > 0
              ? options.map((option) => (
                <MenuItem
                  key={option.value}
                  value={option.value}
                  disabled={!!option.disabled}
                  classes={{
                    root: 'dropdown-menu-item',
                    selected: 'dropdown-menu-item-selected',
                  }}
                >
                  {multiSelect && <Checkbox variant='primary' className='d-checkbox mb-0' label={option.label} checked={!!values.includes(option.value)} name={option.value} />}
                  {!multiSelect && <span>{option.label}</span>}
                </MenuItem>
              ))
              : (
                <div className="dropdown-no-options-dropdown">
                  <em>{t('common.dropdown.noRecordsFound')}</em>
                </div>
              )
          }
        </Select>
      </FormControl>
      {
        error &&
        <div className="fv-plugins-message-container fv-plugins-message-container--enabled invalid-feedback">
          <div data-field={id} data-validator="notEmpty">
            {errorMessage}
          </div>
        </div>
      }
    </div>
  );
};

export default Dropdown;